/*
 * OVIOS Build Your Own
 * Consolidated cart state and product builder runtime.
 * Load once as a JavaScript module from layout/theme.liquid.
 */

export function normalizeShopifyId(value) {
  return String(value ?? '').replace(/^gid:\/\/shopify\/[^/]+\//, '');
}

export function currentProductLines(cart, sourceProductId) {
  const sourceId = normalizeShopifyId(sourceProductId);
  return (cart?.items || []).filter((line) => {
    if (normalizeShopifyId(line.product_id) !== sourceId) return false;
    if (line.properties?._diy_item_role) return false;
    return true;
  });
}

export function diyComponentLines(cart, sourceProductId, groupId = '') {
  const sourceId = normalizeShopifyId(sourceProductId);
  const selectedGroupId = String(groupId || '');
  return (cart?.items || []).filter((line) => {
    const properties = line.properties || {};
    if (properties._diy_item_role !== 'component') return false;
    if (normalizeShopifyId(properties._diy_source_product_id) !== sourceId) return false;
    return !selectedGroupId || String(properties._diy_group_id || '') === selectedGroupId;
  });
}

export function activeDiyGroupId(cart, sourceProductId, preferredGroupId = '') {
  const lines = diyComponentLines(cart, sourceProductId);
  const preferred = String(preferredGroupId || '');
  if (preferred && lines.some((line) => String(line.properties?._diy_group_id || '') === preferred)) {
    return preferred;
  }
  return String(lines[0]?.properties?._diy_group_id || '');
}

export function componentLineForVariant(lines, variantId, productId = '') {
  const id = normalizeShopifyId(variantId);
  const selectedProductId = normalizeShopifyId(productId);
  return (lines || []).find((line) => {
    if (normalizeShopifyId(line.variant_id) !== id) return false;
    return !selectedProductId || normalizeShopifyId(line.product_id) === selectedProductId;
  });
}

export function componentSummary(lines) {
  return (lines || []).reduce(
    (summary, line) => {
      const quantity = Number(line.quantity || 0);
      const fallbackLinePrice = Number(line.line_price ?? Number(line.price || 0) * quantity);
      summary.count += quantity;
      summary.subtotalCents += Number(line.final_line_price ?? fallbackLinePrice);
      return summary;
    },
    { count: 0, subtotalCents: 0 }
  );
}

function deferred() {
  let resolve;
  let reject;
  const promise = new Promise((resolvePromise, rejectPromise) => {
    resolve = resolvePromise;
    reject = rejectPromise;
  });
  return { promise, resolve, reject };
}

function normalizedQuantity(value) {
  const quantity = Number(value);
  return Number.isFinite(quantity) ? Math.max(0, Math.trunc(quantity)) : 0;
}

function mergeAddedItems(cart, addedItems) {
  const items = [...(cart?.items || [])];
  for (const added of addedItems || []) {
    const index = items.findIndex((line) => line.key && line.key === added.key);
    if (index >= 0) {
      items[index] = added;
    } else {
      items.push(added);
    }
  }
  return {
    ...(cart || {}),
    items,
    item_count: items.reduce((count, line) => count + normalizedQuantity(line.quantity), 0),
  };
}

export class CartMutationCoordinator {
  constructor({
    request,
    reconcile,
    sections = () => [],
    sectionsUrl = () => '/',
    delay = 120,
    onBusyChange = () => undefined,
    onMutation = () => undefined,
    onReconcile = () => undefined,
    onError = () => undefined,
    setTimer = (...args) => globalThis.setTimeout(...args),
    clearTimer = (...args) => globalThis.clearTimeout(...args),
  }) {
    if (typeof request !== 'function') throw new TypeError('CartMutationCoordinator requires request');
    if (typeof reconcile !== 'function') throw new TypeError('CartMutationCoordinator requires reconcile');
    this.delay = delay;
    this.request = request;
    this.reconcile = reconcile;
    this.sections = sections;
    this.sectionsUrl = sectionsUrl;
    this.onBusyChange = onBusyChange;
    this.onMutation = onMutation;
    this.onReconcile = onReconcile;
    this.onError = onError;
    this.setTimer = setTimer;
    this.clearTimer = clearTimer;
    this.confirmedCart = { items: [], item_count: 0 };
    this.draftSelections = new Map();
    this.desiredQuantities = new Map();
    this.pendingAdds = new Map();
    this.addsByCard = new Map();
    this.timer = null;
    this.drainPromise = null;
    this.reconcileNeeded = false;
    this.busy = false;
    this.disconnected = false;
  }

  setConfirmedCart(cart) {
    this.confirmedCart = cart && Array.isArray(cart.items) ? cart : { items: [], item_count: 0 };
    return this.confirmedCart;
  }

  setDraft(cardKey, selection) {
    this.draftSelections.set(String(cardKey), {
      variantId: String(selection.variantId),
      quantity: normalizedQuantity(selection.quantity) || 1,
    });
  }

  clearDraft(cardKey) {
    this.draftSelections.delete(String(cardKey));
  }

  queueAdd(cardKey, item) {
    const id = String(cardKey);
    const existing = this.addsByCard.get(id);
    if (existing) return existing.deferred.promise;

    const entry = {
      cardKey: id,
      item: {
        ...item,
        id: Number(item.id),
        quantity: normalizedQuantity(item.quantity) || 1,
        properties: { ...(item.properties || {}) },
      },
      deferred: deferred(),
    };
    this.pendingAdds.set(id, entry);
    this.addsByCard.set(id, entry);
    this.setBusy(true);
    this.scheduleDrain();
    return entry.deferred.promise;
  }

  queueQuantity(lineKey, quantity) {
    const id = String(lineKey);
    if (!id) return Promise.reject(new TypeError('A line item key is required'));
    const waiter = deferred();
    const pending = this.desiredQuantities.get(id);
    if (pending) {
      pending.quantity = normalizedQuantity(quantity);
      pending.waiters.push(waiter);
    } else {
      this.desiredQuantities.set(id, {
        quantity: normalizedQuantity(quantity),
        waiters: [waiter],
      });
    }
    this.setBusy(true);
    this.scheduleDrain();
    return waiter.promise;
  }

  scheduleDrain() {
    if (this.disconnected) return;
    if (this.timer) this.clearTimer(this.timer);
    this.timer = this.setTimer(() => {
      this.timer = null;
      this.ensureDrain();
    }, this.delay);
  }

  hasPendingWork() {
    return this.pendingAdds.size > 0 || this.desiredQuantities.size > 0 || this.reconcileNeeded;
  }

  ensureDrain() {
    if (this.drainPromise) return this.drainPromise;
    if (!this.hasPendingWork()) {
      this.setBusy(false);
      return Promise.resolve();
    }
    this.drainPromise = this.drain().finally(() => {
      this.drainPromise = null;
      if (this.hasPendingWork()) {
        this.ensureDrain();
      } else {
        this.setBusy(false);
      }
    });
    return this.drainPromise;
  }

  async drain() {
    while (this.hasPendingWork() && !this.disconnected) {
      if (this.pendingAdds.size > 0) {
        await this.flushAdds();
        continue;
      }
      if (this.desiredQuantities.size > 0) {
        await this.flushQuantities();
        continue;
      }
      if (this.reconcileNeeded) await this.reconcileCart();
    }
  }

  mutationBaseBody() {
    return {
      sections: [...new Set(this.sections())],
      sections_url: this.sectionsUrl(),
    };
  }

  async flushAdds() {
    const entries = [...this.pendingAdds.values()];
    this.pendingAdds.clear();
    const body = {
      items: entries.map((entry) => entry.item),
      ...this.mutationBaseBody(),
    };
    const operation = {
      type: 'add',
      action: 'add',
      path: 'cart/add.js',
      lines: entries.map((entry) => ({
        merchandiseId: String(entry.item.id),
        quantity: entry.item.quantity,
      })),
      body,
    };

    try {
      const response = await this.request(operation);
      this.setConfirmedCart(mergeAddedItems(this.confirmedCart, response.items));
      this.reconcileNeeded = true;
      await this.onMutation({ operation, response, cart: this.confirmedCart });
      for (const entry of entries) entry.deferred.resolve(response);
    } catch (error) {
      for (const entry of entries) entry.deferred.reject(error);
      this.onError(error, operation);
    } finally {
      for (const entry of entries) {
        if (this.addsByCard.get(entry.cardKey) === entry) this.addsByCard.delete(entry.cardKey);
      }
    }
  }

  async flushQuantities() {
    const entries = [...this.desiredQuantities.entries()];
    this.desiredQuantities.clear();
    const updates = Object.fromEntries(entries.map(([key, entry]) => [key, entry.quantity]));
    const action = Object.values(updates).every((quantity) => quantity === 0) ? 'remove' : 'update';
    const operation = {
      type: 'update',
      action,
      path: 'cart/update.js',
      lines: entries.map(([id, entry]) => ({ id, quantity: entry.quantity })),
      body: {
        updates,
        ...this.mutationBaseBody(),
      },
    };

    try {
      const response = await this.request(operation);
      this.setConfirmedCart(response);
      await this.onMutation({ operation, response, cart: this.confirmedCart });
      for (const [, entry] of entries) {
        for (const waiter of entry.waiters) waiter.resolve(response);
      }
    } catch (error) {
      for (const [, entry] of entries) {
        for (const waiter of entry.waiters) waiter.reject(error);
      }
      this.onError(error, operation);
    }
  }

  async reconcileCart() {
    this.reconcileNeeded = false;
    try {
      this.setConfirmedCart(await this.reconcile());
      await this.onReconcile(this.confirmedCart);
    } catch (error) {
      this.onError(error, { type: 'reconcile', action: 'read' });
    }
  }

  setBusy(value) {
    if (this.busy === value) return;
    this.busy = value;
    this.onBusyChange(value);
  }

  async flush() {
    if (this.timer) this.clearTimer(this.timer);
    this.timer = null;
    do {
      await this.ensureDrain();
    } while (this.drainPromise || this.hasPendingWork());
  }

  disconnect() {
    this.disconnected = true;
    if (this.timer) this.clearTimer(this.timer);
    this.timer = null;
    const error = new DOMException('Coordinator disconnected', 'AbortError');
    for (const entry of this.pendingAdds.values()) entry.deferred.reject(error);
    for (const entry of this.desiredQuantities.values()) {
      for (const waiter of entry.waiters) waiter.reject(error);
    }
    this.pendingAdds.clear();
    this.desiredQuantities.clear();
    this.addsByCard.clear();
    this.setBusy(false);
  }
}

const MUTATION_DELAY_MS = 120;
const IMAGE_WIDTHS = [240, 360, 480, 720];

function numeric(value, fallback = 0) {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
}

function variantImageSrcset(imageUrl) {
  try {
    const source = new URL(imageUrl, window.location.href);
    if (!source.searchParams.has('width')) return source.href;
    return IMAGE_WIDTHS.map((width) => {
      const candidate = new URL(source);
      candidate.searchParams.set('width', String(width));
      return `${candidate.href} ${width}w`;
    }).join(', ');
  } catch (_error) {
    return imageUrl;
  }
}

function element(tagName, className, text) {
  const node = document.createElement(tagName);
  if (className) node.className = className;
  if (text !== undefined) node.textContent = text;
  return node;
}

class DiyProductBuilder extends HTMLElement {
  connectedCallback() {
    if (this.dataset.initialized === 'true') return;
    this.dataset.initialized = 'true';
    if (!this.id) this.id = `DiyProductBuilder-${this.dataset.sourceProductId}`;

    this.dialog = this.querySelector('[data-diy-dialog]');
    this.currentList = this.querySelector('[data-diy-current-list]');
    this.currentItems = this.querySelector('[data-diy-current-items]');
    this.status = this.querySelector('[data-diy-status]');
    this.error = this.querySelector('[data-diy-error]');
    this.dialogError = this.querySelector('[data-diy-dialog-error]');
    this.loading = this.querySelector('[data-diy-loading]');
    this.viewCartButton = this.querySelector('[data-diy-view-cart]');
    this.abortController = new AbortController();
    this.cart = { items: [], item_count: 0 };
    this.componentLines = [];
    this.sourceLines = [];
    this.cardStates = new Map();
    this.cardVersions = new Map();
    this.sourceVariants = this.parseJson('[data-diy-source-variants]', []);
    this.sourceVariantMap = new Map(this.sourceVariants.map((variant) => [String(variant.id), variant]));
    this.groupStorageKey = `diy-group:${this.dataset.sourceProductId}`;
    this.groupId = this.readStoredGroupId();
    this.rootUrl = window.Shopify?.routes?.root || '/';
    if (!this.rootUrl.endsWith('/')) this.rootUrl += '/';
    this.coordinator = new CartMutationCoordinator({
      delay: MUTATION_DELAY_MS,
      request: (operation) => this.requestCartMutation(operation),
      reconcile: () => this.readCart(),
      sections: () => this.sectionIds(),
      sectionsUrl: () => window.location.pathname,
      onBusyChange: (busy) => this.handleCoordinatorBusy(busy),
      onMutation: (payload) => this.handleMutation(payload),
      onReconcile: (cart) => this.syncCart(cart),
      onError: (caught, operation) => {
        if (operation?.type === 'reconcile') this.showError(caught);
      },
    });

    const { signal } = this.abortController;
    this.querySelector('[data-diy-open]')?.addEventListener('click', () => this.openDialog(), { signal });
    this.querySelector('[data-diy-close]')?.addEventListener('click', () => this.dialog?.close(), { signal });
    this.viewCartButton?.addEventListener('click', () => this.openCartDrawer(), { signal });
    this.dialog?.addEventListener('close', () => this.restoreFocus(), { signal });

    for (const card of this.cards) {
      const cardKey = this.cardKey(card);
      const variant = card.querySelector('[data-diy-variant]');
      const quantity = card.querySelector('[data-diy-quantity]');
      const option = variant?.selectedOptions?.[0];
      this.coordinator.setDraft(cardKey, {
        variantId: option?.value || '',
        quantity: numeric(quantity?.value, 1),
      });
      card.querySelector('[data-diy-add]')?.addEventListener('click', () => this.toggleCard(card), { signal });
      card.querySelector('[data-diy-remove]')?.addEventListener('click', () => this.removeCard(card), { signal });
      variant?.addEventListener('change', () => this.changeDraftVariant(card), { signal });
      quantity?.addEventListener('change', () => this.changeCardQuantity(card), { signal });
    }

    document.addEventListener('cart:change', this.handleExternalCartUpdate, { signal });
    document.addEventListener('cart:refresh', this.handleCartRefresh, { signal });
    window.addEventListener('pageshow', this.handlePageShow, { signal });
    this.refreshCart().catch((caught) => this.showError(caught));
  }

  disconnectedCallback() {
    this.abortController?.abort();
    this.coordinator?.disconnect();
  }

  get cards() {
    return [...this.querySelectorAll('[data-diy-card]')];
  }

  get sourceProductId() {
    return String(this.dataset.sourceProductId || '');
  }

  cardKey(card) {
    return String(card.dataset.componentProductId || '');
  }

  nextCardVersion(cardKey) {
    const version = (this.cardVersions.get(cardKey) || 0) + 1;
    this.cardVersions.set(cardKey, version);
    return version;
  }

  isCurrentCardVersion(cardKey, version) {
    return this.cardVersions.get(cardKey) === version;
  }

  parseJson(selector, fallback) {
    try {
      return JSON.parse(this.querySelector(selector)?.textContent || '');
    } catch (_error) {
      return fallback;
    }
  }

  readStoredGroupId() {
    try {
      return window.sessionStorage.getItem(this.groupStorageKey) || '';
    } catch (_error) {
      return '';
    }
  }

  storeGroupId() {
    try {
      window.sessionStorage.setItem(this.groupStorageKey, this.groupId);
    } catch (_error) {
      return;
    }
  }

  createGroupId() {
    const random = globalThis.crypto?.randomUUID?.() || `${Date.now()}-${Math.random().toString(36).slice(2)}`;
    return `diy-${this.sourceProductId}-${random}`;
  }

  handlePageShow = (event) => {
    if (event.persisted) this.refreshCart().catch((caught) => this.showError(caught));
  };

  handleExternalCartUpdate = (event) => {
    if (event.detail?.sourceId === this.id) return;
    if (this.coordinator.busy) return;
    if (Array.isArray(event.detail?.cart?.items)) {
      this.syncCart(event.detail.cart);
    } else {
      this.refreshCart().catch((caught) => this.showError(caught));
    }
  };

  handleCartRefresh = () => {
    if (this.coordinator.busy) return;
    this.refreshCart().catch((caught) => this.showError(caught));
  };

  async requestJson(path, init = {}) {
    const response = await fetch(`${this.rootUrl}${path}`, {
      ...init,
      headers: {
        Accept: 'application/json',
        ...(init.body ? { 'Content-Type': 'application/json' } : {}),
        ...(init.headers || {}),
      },
    });
    const result = await response.json();
    if (!response.ok || result.status || result.errors) {
      throw new Error(result.description || result.message || result.errors || this.dataset.errorCartUpdate);
    }
    return result;
  }

  readCart() {
    return this.requestJson('cart.js', { method: 'GET', cache: 'no-store' });
  }

  async refreshCart() {
    const cart = await this.readCart();
    this.syncCart(cart);
    return cart;
  }

  syncCart(cart) {
    this.cart = cart;
    this.coordinator.setConfirmedCart(cart);
    const existingGroupId = activeDiyGroupId(cart, this.sourceProductId, this.groupId);
    if (existingGroupId) this.groupId = existingGroupId;
    if (!this.groupId) this.groupId = this.createGroupId();
    this.storeGroupId();
    this.componentLines = diyComponentLines(cart, this.sourceProductId, this.groupId);
    this.sourceLines = currentProductLines(cart, this.sourceProductId);
    this.render();
  }

  render() {
    this.renderCurrentProduct();
    for (const card of this.cards) this.renderCard(card);
    this.renderSummary();
  }

  renderSummary() {
    const summary = componentSummary(this.componentLines);
    let countValue = summary.count;
    let subtotalCents = summary.subtotalCents;
    for (const state of this.cardStates.values()) {
      if (state.mode === 'adding') {
        countValue += state.target;
        subtotalCents += state.target * state.unitPrice;
      } else if (state.mode === 'updating') {
        countValue += state.target - state.confirmedQuantity;
        subtotalCents += (state.target - state.confirmedQuantity) * state.unitPrice;
      } else if (state.mode === 'removing') {
        countValue -= state.confirmedQuantity;
        subtotalCents -= state.confirmedQuantity * state.unitPrice;
      }
    }
    const count = this.querySelector('[data-diy-count]');
    const subtotal = this.querySelector('[data-diy-subtotal]');
    if (count) count.textContent = String(Math.max(0, countValue));
    if (subtotal) subtotal.textContent = this.formatMoney(Math.max(0, subtotalCents));
    if (this.status) {
      if (this.coordinator.busy || this.cardStates.size > 0) {
        this.status.textContent = `${this.dataset.labelUpdating}…`;
      } else {
        this.status.textContent = summary.count
          ? `${this.dataset.labelSelected || ''} ${summary.count}`.trim()
          : '';
      }
    }
  }

  renderCurrentProduct() {
    if (!this.currentList || !this.currentItems) return;
    this.currentItems.replaceChildren();
    const showCurrentProduct = this.dataset.showCurrentProduct === 'true' && this.sourceLines.length > 0;
    this.currentList.hidden = !showCurrentProduct;
    if (!showCurrentProduct) return;

    for (const line of this.sourceLines) {
      const row = element('article', 'diy-builder__current-row');
      const media = element('div', 'diy-builder__current-media');
      const imageUrl = line.featured_image?.url || line.image;
      if (imageUrl) {
        const image = element('img', 'diy-builder__current-image');
        image.src = imageUrl;
        image.alt = line.featured_image?.alt || line.product_title || '';
        image.loading = 'lazy';
        media.append(image);
      }

      const details = element('div', 'diy-builder__current-details');
      details.append(element('strong', '', line.product_title || line.title || ''));
      if (line.variant_title && line.variant_title !== this.dataset.labelDefaultVariant) {
        details.append(element('span', '', `${this.dataset.labelColor}: ${line.variant_title}`));
      }
      if (line.sku) details.append(element('span', '', `${this.dataset.labelSku}: ${line.sku}`));
      const sourceVariant = this.sourceVariantMap.get(String(line.variant_id));
      const stockLabel = sourceVariant?.available === false ? this.dataset.labelOutOfStock : this.dataset.labelInStock;
      details.append(element('span', '', `${this.dataset.labelAvailability}: ${stockLabel}`));

      const price = element(
        'strong',
        'diy-builder__current-price',
        `${this.formatMoney(numeric(line.final_price ?? line.price))} × ${numeric(line.quantity, 1)}`
      );
      const remove = element('button', 'diy-builder__current-remove', '×');
      remove.type = 'button';
      remove.setAttribute(
        'aria-label',
        this.interpolate(this.dataset.labelRemoveCurrent, { product: line.product_title || line.title || '' })
      );
      remove.addEventListener('click', () => this.removeCurrentLine(line, remove), {
        signal: this.abortController.signal,
      });

      row.append(media, details, price, remove);
      this.currentItems.append(row);
    }
  }

  componentLineForCard(card) {
    const productId = normalizeShopifyId(card.dataset.componentProductId);
    return this.componentLines.find((line) => normalizeShopifyId(line.product_id) === productId);
  }

  selectedOption(card) {
    return card.querySelector('[data-diy-variant]')?.selectedOptions?.[0] || null;
  }

  ensureQuantityOptions(select, maximum, value) {
    if (!(select instanceof HTMLSelectElement)) return 1;
    const limit = Math.max(1, Math.min(10, numeric(maximum, 10)));
    const selected = Math.max(1, Math.min(limit, numeric(value, 1)));
    if (numeric(select.dataset.maximum) !== limit || select.options.length !== limit) {
      const fragment = document.createDocumentFragment();
      for (let quantity = 1; quantity <= limit; quantity += 1) {
        const option = document.createElement('option');
        option.value = String(quantity);
        option.textContent = String(quantity);
        fragment.append(option);
      }
      select.replaceChildren(fragment);
      select.dataset.maximum = String(limit);
    }
    select.value = String(selected);
    return selected;
  }

  renderCard(card) {
    const variant = card.querySelector('[data-diy-variant]');
    const quantity = card.querySelector('[data-diy-quantity]');
    if (!(variant instanceof HTMLSelectElement) || !(quantity instanceof HTMLSelectElement)) return;

    const cardKey = this.cardKey(card);
    const state = this.cardStates.get(cardKey);
    const productLine = this.componentLineForCard(card);
    if (productLine) {
      const matchingOption = [...variant.options].find((option) => option.value === String(productLine.variant_id));
      if (matchingOption) variant.value = matchingOption.value;
    }

    const option = variant.selectedOptions[0];
    if (!option) return;
    const exactLine = productLine
      ? componentLineForVariant(this.componentLines, option.value, card.dataset.componentProductId)
      : undefined;
    const added = Boolean(exactLine);
    const available = option.dataset.available === 'true';
    const confirmedQuantity = numeric(exactLine?.quantity);
    const draft = this.coordinator.draftSelections.get(cardKey) || { variantId: option.value, quantity: 1 };
    const requestedQuantity = state?.mode === 'removing' ? confirmedQuantity : state?.target;
    const displayedQuantity = requestedQuantity ?? (added ? confirmedQuantity : draft.quantity);
    const selectedQuantity = this.ensureQuantityOptions(quantity, option.dataset.quantityMax, displayedQuantity);
    if (!added && !state) {
      this.coordinator.setDraft(cardKey, { variantId: option.value, quantity: selectedQuantity });
    }

    const add = card.querySelector('[data-diy-add]');
    const remove = card.querySelector('[data-diy-remove]');
    const price = card.querySelector('[data-diy-price]');
    const image = card.querySelector('[data-diy-image]');
    const adding = state?.mode === 'adding';
    const removing = state?.mode === 'removing';
    const pending = Boolean(state);

    variant.disabled = added || pending;
    quantity.disabled = adding || removing;
    if (add) {
      if (adding) {
        add.textContent = this.dataset.labelAdding;
        add.disabled = true;
      } else if (added) {
        add.textContent = `✓ ${this.dataset.labelAdded}`;
        add.disabled = pending;
        add.setAttribute(
          'aria-label',
          this.interpolate(this.dataset.labelRemoveComponent || 'Remove __product__ from cart', {
            product: card.dataset.componentTitle || '',
          })
        );
      } else {
        add.textContent = available ? this.dataset.labelAdd : this.dataset.labelSoldOut;
        add.disabled = !available;
        add.removeAttribute('aria-label');
      }
    }
    if (remove) {
      remove.hidden = !added;
      remove.disabled = pending;
      remove.toggleAttribute('aria-busy', removing);
    }
    if (price) price.textContent = option.dataset.priceLabel || this.formatMoney(numeric(option.dataset.priceCents));
    if (image && option.dataset.image) {
      image.srcset = variantImageSrcset(option.dataset.image);
      image.src = option.dataset.image;
      image.alt = option.dataset.imageAlt || card.dataset.componentTitle || '';
    }
    card.toggleAttribute('data-added', added);
    card.toggleAttribute('data-pending', pending);
    card.toggleAttribute('aria-busy', pending);
  }

  changeDraftVariant(card) {
    this.clearError();
    const cardKey = this.cardKey(card);
    const option = this.selectedOption(card);
    const quantity = card.querySelector('[data-diy-quantity]');
    if (!option || !(quantity instanceof HTMLSelectElement)) return;
    const selectedQuantity = this.ensureQuantityOptions(quantity, option.dataset.quantityMax, 1);
    this.coordinator.setDraft(cardKey, { variantId: option.value, quantity: selectedQuantity });
    this.renderCard(card);
  }

  changeCardQuantity(card) {
    this.clearError();
    const cardKey = this.cardKey(card);
    const option = this.selectedOption(card);
    const quantity = card.querySelector('[data-diy-quantity]');
    if (!option || !(quantity instanceof HTMLSelectElement)) return;
    const target = this.ensureQuantityOptions(quantity, option.dataset.quantityMax, quantity.value);
    const line = this.componentLineForCard(card);
    if (!line) {
      this.coordinator.setDraft(cardKey, { variantId: option.value, quantity: target });
      this.renderCard(card);
      return;
    }
    if (target === numeric(line.quantity) && !this.cardStates.has(cardKey)) return;
    this.queueCardQuantity(card, line, target, 'updating');
  }

  toggleCard(card) {
    const line = this.componentLineForCard(card);
    if (line?.key) {
      this.removeCard(card);
      return;
    }
    this.addCard(card);
  }

  addCard(card) {
    this.clearError();
    const cardKey = this.cardKey(card);
    const option = this.selectedOption(card);
    const quantity = card.querySelector('[data-diy-quantity]');
    if (!option || option.dataset.available !== 'true' || !(quantity instanceof HTMLSelectElement)) return;
    const target = this.ensureQuantityOptions(quantity, option.dataset.quantityMax, quantity.value);
    const version = this.nextCardVersion(cardKey);
    this.cardStates.set(cardKey, {
      mode: 'adding',
      target,
      unitPrice: numeric(option.dataset.priceCents),
    });
    this.renderCard(card);
    this.renderSummary();

    this.coordinator
      .queueAdd(cardKey, {
        id: numeric(option.value),
        quantity: target,
        properties: {
          _diy_source_product_id: this.sourceProductId,
          _diy_group_id: this.groupId,
          _diy_item_role: 'component',
        },
      })
      .then(() => {
        if (!this.isCurrentCardVersion(cardKey, version)) return;
        this.cardStates.delete(cardKey);
        this.coordinator.clearDraft(cardKey);
        this.clearError();
        this.render();
      })
      .catch((caught) => {
        if (!this.isCurrentCardVersion(cardKey, version)) return;
        this.cardStates.delete(cardKey);
        this.showError(caught);
        this.render();
      });
  }

  removeCard(card) {
    this.clearError();
    const line = this.componentLineForCard(card);
    if (!line?.key) return;
    this.queueCardQuantity(card, line, 0, 'removing');
  }

  queueCardQuantity(card, line, target, mode) {
    const cardKey = this.cardKey(card);
    const version = this.nextCardVersion(cardKey);
    const unitPrice = numeric(line.final_price ?? line.price);
    this.cardStates.set(cardKey, {
      mode,
      target,
      lineKey: line.key,
      confirmedQuantity: numeric(line.quantity),
      unitPrice,
    });
    this.renderCard(card);
    this.renderSummary();

    this.coordinator
      .queueQuantity(line.key, target)
      .then(() => {
        if (!this.isCurrentCardVersion(cardKey, version)) return;
        this.cardStates.delete(cardKey);
        if (target === 0) {
          const option = this.selectedOption(card);
          this.coordinator.setDraft(cardKey, { variantId: option?.value || '', quantity: 1 });
        }
        this.clearError();
        this.render();
      })
      .catch((caught) => {
        if (!this.isCurrentCardVersion(cardKey, version)) return;
        this.cardStates.delete(cardKey);
        this.showError(caught);
        this.render();
      });
  }

  sectionIds() {
    const drawer = document.querySelector('cart-drawer#cart-drawer');
    const section = drawer?.closest('.shopify-section');
    const id = section?.id?.replace(/^shopify-section-/, '');
    return id ? [id] : [];
  }

  async requestCartMutation(operation) {
    return this.requestJson(operation.path, {
      method: 'POST',
      body: JSON.stringify(operation.body),
    });
  }

  handleMutation({ operation, response, cart }) {
    this.syncCart(cart);
    const drawerSectionId = this.sectionIds()[0];
    const sectionHtml = drawerSectionId && response.sections?.[drawerSectionId];
    if (typeof sectionHtml === 'string') {
      this.drawerNeedsRefresh = false;
      document.documentElement.dispatchEvent(new CustomEvent('cart:change', {
        bubbles: true,
        detail: {
          baseEvent: 'variant:add',
          cart: { ...cart, sections: response.sections },
          sourceId: this.id,
        },
      }));
    } else {
      this.drawerNeedsRefresh = true;
      document.dispatchEvent(new CustomEvent('cart:refresh'));
    }
  }

  handleCoordinatorBusy(busy) {
    this.toggleAttribute('data-sync-busy', busy);
    this.renderSummary();
  }

  async removeCurrentLine(line, button) {
    if (!line?.key || button.disabled) return;
    this.clearError();
    button.disabled = true;
    button.setAttribute('aria-busy', 'true');
    try {
      await this.coordinator.queueQuantity(line.key, 0);
      this.clearError();
    } catch (caught) {
      this.showError(caught);
    } finally {
      button.disabled = false;
      button.removeAttribute('aria-busy');
    }
  }

  async openDialog() {
    if (!this.dialog || this.dialog.open) return;
    this.previouslyFocused = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    this.dialog.showModal();
    this.setLoading(true);
    try {
      await this.coordinator.flush();
      await this.refreshCart();
      this.clearError();
      this.querySelector('[data-diy-close]')?.focus();
    } catch (caught) {
      this.showError(caught);
    } finally {
      this.setLoading(false);
    }
  }

  restoreFocus() {
    this.previouslyFocused?.focus();
    this.previouslyFocused = null;
  }

  async openCartDrawer() {
    if (!this.viewCartButton || this.viewCartButton.disabled) return;
    this.viewCartButton.disabled = true;
    this.viewCartButton.setAttribute('aria-busy', 'true');
    try {
      await this.coordinator.flush();
      if (this.drawerNeedsRefresh) {
        const drawer = document.querySelector('cart-drawer#cart-drawer');
        if (typeof drawer?._onCartRefresh === 'function') {
          await drawer._onCartRefresh();
          this.drawerNeedsRefresh = false;
        }
      }
      await this.waitForDrawerMorph();
      this.revealCartDrawer();
    } catch (caught) {
      this.showError(caught);
    } finally {
      this.viewCartButton.disabled = false;
      this.viewCartButton.removeAttribute('aria-busy');
    }
  }

  waitForDrawerMorph() {
    return new Promise((resolve) => requestAnimationFrame(() => requestAnimationFrame(resolve)));
  }

  revealCartDrawer() {
    const drawer = document.querySelector('cart-drawer#cart-drawer');
    if (window.themeVariables?.settings?.cartType !== 'drawer' || !drawer || typeof drawer.show !== 'function') {
      window.location.assign(`${this.rootUrl}cart`);
      return;
    }
    this.dialog?.close();
    drawer.show();
  }

  setLoading(loading) {
    if (this.loading) this.loading.hidden = !loading;
    this.toggleAttribute('data-loading', loading);
  }

  showError(caught) {
    const message = caught?.message || this.dataset.errorCartUpdate;
    if (this.error) {
      this.error.textContent = message;
      this.error.hidden = false;
    }
    if (this.dialogError) {
      this.dialogError.textContent = message;
      this.dialogError.hidden = false;
    }
  }

  clearError() {
    if (this.error) {
      this.error.textContent = '';
      this.error.hidden = true;
    }
    if (this.dialogError) {
      this.dialogError.textContent = '';
      this.dialogError.hidden = true;
    }
  }

  interpolate(template, values) {
    let result = String(template || '');
    for (const [key, value] of Object.entries(values)) {
      result = result.replaceAll(`__${key}__`, String(value));
    }
    return result;
  }

  formatMoney(cents) {
    const amount = numeric(cents) / 100;
    const format = this.dataset.moneyFormat || '${{amount}}';
    const match = format.match(/\{\{\s*(\w+)\s*\}\}/);
    if (!match) return `$${amount.toFixed(2)}`;
    const settings = {
      amount: [2, ',', '.'],
      amount_no_decimals: [0, ',', '.'],
      amount_with_comma_separator: [2, '.', ','],
      amount_no_decimals_with_comma_separator: [0, '.', ','],
      amount_with_space_separator: [2, ' ', ','],
      amount_no_decimals_with_space_separator: [0, ' ', ','],
      amount_with_period_and_space_separator: [2, ' ', '.'],
      amount_no_decimals_with_period_and_space_separator: [0, ' ', '.'],
      amount_with_apostrophe_separator: [2, "'", '.'],
    };
    const [precision, thousands, decimal] = settings[match[1]] || settings.amount;
    const fixed = amount.toFixed(precision).split('.');
    const integer = fixed[0].replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
    const value = precision ? `${integer}${decimal}${fixed[1]}` : integer;
    return format.replace(match[0], value);
  }
}

if (!customElements.get('diy-product-builder')) {
  customElements.define('diy-product-builder', DiyProductBuilder);
}
